/**
 * Moving a customer who is still waiting: to another service's line, or to
 * another place in their own, before any desk has called them.
 *
 * The rules are the server's (engine/flow.go, Move) and are mirrored here, where a
 * test runs them, so the panel never offers a move the server will refuse. A move
 * targets a service, never a queue: the branch's routing chooses the line, as it
 * does at issue. The placements are a transfer's, from lib/transfer.
 */
import type { Ticket } from './types'
import { isHeld } from './branchState'
import { t, type StaffLanguage } from './staffText'
import { placementBody, whyNotDelay, type Placement } from './transfer'

export interface MoveChoice {
  serviceId: string
  placement: Placement
  minutes: number
  /** 'desk' sends them to one desk, which calls them first; absent is a service. */
  to?: 'service' | 'desk'
  counterId?: string
}

/** The places a send to a desk offers, its default first. */
export const DESK_PLACEMENTS: readonly Placement[] = ['back', 'front']

export function currentServiceId(ticket: Pick<Ticket, 'transactions' | 'currentTransaction'>): string {
  return ticket.transactions[ticket.currentTransaction]?.serviceId ?? ''
}

/** Whether a clerk put this customer somewhere other than their waiting alone
 *  would: sent to the front, or held back and not yet returned. Keeping their
 *  place in the same line undoes that, so it is a move worth making. */
export function placedByAClerk(ticket: Pick<Ticket, 'front' | 'held' | 'backAt'>, now: number = Date.now()): boolean {
  return !!ticket.front || isHeld(ticket as Ticket, now)
}

/** Why this move would be refused, in words, or '' when it would not. `placed`
 *  is placedByAClerk for the ticket being moved. */
export function whyNotMove(currentService: string, choice: MoveChoice, placed: boolean, lang: StaffLanguage): string {
  if (choice.to === 'desk') {
    return choice.counterId ? '' : t('floor.chooseDesk', lang)
  }
  if (!choice.serviceId) return t('floor.chooseServiceToWaitFor', lang)
  if (choice.placement === 'delay') {
    const delay = whyNotDelay(choice.minutes, lang)
    if (delay) return delay
  }
  if (choice.serviceId === currentService && choice.placement === 'keep' && !placed) {
    return t('floor.alreadyWaitingThere', lang)
  }
  return ''
}

export function moveBody(choice: MoveChoice): {
  serviceId?: string
  counterId?: string
  placement?: Placement
  delaySec?: number
} {
  if (choice.to === 'desk') {
    const placement = DESK_PLACEMENTS.includes(choice.placement) ? choice.placement : DESK_PLACEMENTS[0]!
    return { counterId: choice.counterId ?? '', ...placementBody(placement, 'desk', choice.minutes) }
  }
  return {
    serviceId: choice.serviceId,
    ...placementBody(choice.placement, 'service', choice.minutes),
  }
}
